import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  enableNetwork,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Transaction, Budget, SavingsGoal } from '../types';
import { SecurityService } from './securityService';

export interface UserData {
  transactions: Transaction[];
  budgets: Budget[];
  savingsGoals: SavingsGoal[];
  lastSync: any;
}

export class RealTimeSyncService {
  private userId: string;
  private unsubscribeCallbacks: (() => void)[] = [];
  private isOnline: boolean = navigator.onLine;

  constructor(userId: string) {
    this.userId = userId;
    this.setupNetworkListener();
  }

  private setupNetworkListener(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      enableNetwork(db!);
      console.log('✅ Connexion rétablie - Synchronisation en cours...');
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('🔄 Mode hors ligne - Les données seront synchronisées à la reconnexion');
    });
  }

  private ensureFirebaseConfigured(): void {
    if (!db) {
      throw new Error('Firebase non configuré');
    }
  }

  // Synchronisation complète des données utilisateur avec validation de sécurité
  async syncAllData(data: Omit<UserData, 'lastSync'>): Promise<void> {
    this.ensureFirebaseConfigured();
    
    try {
      // Validation de sécurité des données
      const validationResult = SecurityService.validateUserData(data);
      if (!validationResult.isValid) {
        console.error('❌ Données invalides:', validationResult.errors);
        throw new Error(`Données invalides: ${validationResult.errors.join(', ')}`);
      }

      // Validation de l'intégrité des données
      if (!SecurityService.validateDataIntegrity(data)) {
        console.error('❌ Intégrité des données compromise');
        throw new Error('Intégrité des données compromise');
      }

      const batch = writeBatch(db!);
      const userDocRef = doc(db!, 'users', this.userId);

      // Sauvegarder toutes les données dans un seul document pour la cohérence
      const secureData = {
        transactions: data.transactions || [],
        budgets: data.budgets || [],
        savingsGoals: data.savingsGoals || [],
        lastSync: serverTimestamp(),
        // Métadonnées de sécurité
        securityVersion: '1.0',
        dataIntegrity: true
      };

      batch.set(userDocRef, secureData, { merge: true });

      await batch.commit();
      console.log('✅ Synchronisation sécurisée réussie');
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation:', error);
      throw new Error('Impossible de synchroniser les données');
    }
  }

  // Charger toutes les données depuis Firebase
  async loadAllData(): Promise<UserData | null> {
    this.ensureFirebaseConfigured();
    
    try {
      const userDocRef = doc(db!, 'users', this.userId);
      const docSnap = await getDoc(userDocRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as UserData;
        
        // Convertir les dates Firestore en objets Date JavaScript
        if (data.transactions) {
          data.transactions = data.transactions.map(t => ({
            ...t,
            date: t.date instanceof Object && 'toDate' in t.date 
              ? (t.date as any).toDate() 
              : new Date(t.date)
          }));
        }
        
        console.log('✅ Données chargées depuis Firebase:', {
          transactions: data.transactions?.length || 0,
          budgets: data.budgets?.length || 0,
          savingsGoals: data.savingsGoals?.length || 0
        });
        
        return data;
      }
      
      console.log('ℹ️ Aucune donnée trouvée sur Firebase');
      return null;
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error);
      throw new Error('Impossible de charger les données');
    }
  }

  // Écouter les changements en temps réel
  setupRealTimeSync(onDataUpdate: (data: UserData) => void): void {
    this.ensureFirebaseConfigured();
    
    const userDocRef = doc(db!, 'users', this.userId);
    
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as UserData;
        
        // Convertir les dates
        if (data.transactions) {
          data.transactions = data.transactions.map(t => ({
            ...t,
            date: t.date instanceof Object && 'toDate' in t.date 
              ? (t.date as any).toDate() 
              : new Date(t.date)
          }));
        }
        
        console.log('🔄 Mise à jour en temps réel reçue');
        onDataUpdate(data);
      }
    }, (error) => {
      console.error('❌ Erreur de synchronisation temps réel:', error);
    });

    this.unsubscribeCallbacks.push(unsubscribe);
  }

  // Synchronisation automatique périodique
  setupAutoSync(
    getData: () => { transactions: Transaction[]; budgets: Budget[]; savingsGoals: SavingsGoal[] },
    intervalMinutes: number = 5
  ): void {
    const syncInterval = setInterval(async () => {
      if (this.isOnline) {
        try {
          const currentData = getData();
          await this.syncAllData(currentData);
          console.log('🔄 Synchronisation automatique réussie');
        } catch (error) {
          console.error('❌ Échec de la synchronisation automatique:', error);
        }
      }
    }, intervalMinutes * 60 * 1000);

    // Nettoyer l'intervalle lors de la destruction
    this.unsubscribeCallbacks.push(() => clearInterval(syncInterval));
  }

  // Forcer une synchronisation immédiate
  async forceSyncNow(data: Omit<UserData, 'lastSync'>): Promise<void> {
    if (!this.isOnline) {
      throw new Error('Synchronisation impossible : pas de connexion internet');
    }

    await this.syncAllData(data);
  }

  // Vérifier si les données locales sont plus récentes
  async isLocalDataNewer(localLastModified: Date): Promise<boolean> {
    try {
      const cloudData = await this.loadAllData();
      if (!cloudData || !cloudData.lastSync) {
        return true; // Les données locales sont plus récentes si rien dans le cloud
      }

      const cloudLastSync = cloudData.lastSync.toDate?.() || new Date(cloudData.lastSync);
      return localLastModified > cloudLastSync;
    } catch (error) {
      console.error('Erreur lors de la comparaison des dates:', error);
      return false;
    }
  }

  // Nettoyer les listeners
  cleanup(): void {
    this.unsubscribeCallbacks.forEach(unsubscribe => unsubscribe());
    this.unsubscribeCallbacks = [];
  }

  // Obtenir le statut de synchronisation
  getSyncStatus(): { isOnline: boolean; hasListeners: boolean } {
    return {
      isOnline: this.isOnline,
      hasListeners: this.unsubscribeCallbacks.length > 0
    };
  }
}